import * as React from "react";
import {AIAgentTask, AiScriptChangeIteration} from "../../../../../generated/api";
import {Button, Dropdown, Message} from "semantic-ui-react";
import {FileViewer} from "../../../../util/fileViewer/FileViewer";
import {useState} from "react";
import {ConversionResultsComparisonModal} from "./ConversionResultsComparisonModal";


export interface AiAgentTaskIterationConversationResultFilesProps {
    aiAgentTask: AIAgentTask | null
}

export function AiAgentTaskIterationConversationResultFiles(props: AiAgentTaskIterationConversationResultFilesProps) {

    const [selectedConversionFile, setSelectedConversionFile] = React.useState<{
        buffer: ArrayBuffer,
        fileName: string
    } | null>(null);
    const [selectedIterationIndex, setSelectedIterationIndex] = React.useState<number | null>(null);
    const [selectedFileOptionValue, setSelectedFileOptionValue] = React.useState<number | null>(null);
    const [showConversionComparisonModal, setShowConversionComparisonModal] = useState<boolean>(false);
    const [iterationsWithConversion, setIterationsWithConversion] = React.useState<AiScriptChangeIteration[]>([]);

    React.useEffect(() => {
        setIterationsWithConversion(props.aiAgentTask?.iterations?.filter(
            iteration => iteration.queuedEventConversionResponse?.resultFiles?.length > 0
        ) || [])
    }, [props.aiAgentTask]);


    function processAndSetFile(fileData: { base64Content?: string | null, fileName?: string | null } | null, fileIndexForName: number) {
        if (fileData && fileData.base64Content) {
            try {
                const binaryString = atob(fileData.base64Content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                setSelectedConversionFile({
                    buffer: bytes.buffer,
                    fileName: fileData.fileName || `File ${fileIndexForName + 1}`
                });
            } catch (error) {
                console.error("Error processing file content:", error);
                setSelectedConversionFile(null);
            }
        } else {
            setSelectedConversionFile(null);
        }
    }

    if (iterationsWithConversion.length === 0) {
        return (
            <Message style={{color: 'black'}}>
                <Message.Header>No Conversion Results Available</Message.Header>
                <p>No file conversion results are available for the current task.</p>
            </Message>
        );
    }

    //create options for iteration dropdown
    const iterationOptions = iterationsWithConversion.map((iteration, index) => {
        const timestamp = iteration.timestamp
            ? new Date(iteration.timestamp).toLocaleString()
            : `Iteration ${index + 1}`; // Fallback naming
        const fileCount = iteration.queuedEventConversionResponse?.resultFiles?.length || 0;
        const iterationSource = iteration.functionCallOutput.executeFunc || "Unknown Source";

        return {
            key: `iter-${index}`, // Ensure unique key for React
            text: `${timestamp} - [${iterationSource}] - ${fileCount} file(s)`,
            value: index // This is the index in iterationsWithConversion array
        };
    });

    const currentIterationData = selectedIterationIndex !== null
        ? iterationsWithConversion[selectedIterationIndex]
        : null;

    const fileOptions = currentIterationData?.queuedEventConversionResponse?.resultFiles?.map((file, index) => ({
        key: `file-${selectedIterationIndex}-${index}`, // Ensure unique key
        text: file.fileName || `File ${index + 1}`,
        value: index
    })) || [];

    // Handle iteration selection
    const handleIterationChange = (_, data) => {
        const newIterArrayIndex = data.value as number;
        setSelectedIterationIndex(newIterArrayIndex);
        setSelectedFileOptionValue(null);
        setSelectedConversionFile(null);

        const newlySelectedIterationData = iterationsWithConversion[newIterArrayIndex];
        const filesInNewIter = newlySelectedIterationData?.queuedEventConversionResponse?.resultFiles;

        if (filesInNewIter && filesInNewIter.length > 0) {
            const firstFileIndexInOptions = 0;
            setSelectedFileOptionValue(firstFileIndexInOptions);
            processAndSetFile(filesInNewIter[firstFileIndexInOptions], firstFileIndexInOptions);
        }
    };

    // Handle file selection
    const handleFileChange = (_, data) => {
        const selectedFileIndexInOptions = data.value as number;
        setSelectedFileOptionValue(selectedFileIndexInOptions);

        if (selectedIterationIndex !== null && currentIterationData) {
            const fileData = currentIterationData.queuedEventConversionResponse?.resultFiles?.[selectedFileIndexInOptions];
            processAndSetFile(fileData, selectedFileIndexInOptions);
        } else {
            setSelectedConversionFile(null);
        }
    };

    const status = currentIterationData?.queuedEventConversionResponse?.status || "";
    const statusMessage = currentIterationData?.queuedEventConversionResponse?.statusMessage || "";
    const activeConverter = currentIterationData?.queuedEventConversionResponse?.activeConverter || "";

    return (
        <>
            <div style={{marginBottom: "10px"}}>
                <div style={{display: "flex", gap: "5px", marginBottom: "5px"}}>
                    <div style={{flex: 1}}>
                        <label>Select Iteration:</label>
                        <Dropdown
                            fluid
                            selection
                            options={iterationOptions}
                            value={selectedIterationIndex}
                            onChange={handleIterationChange}
                            placeholder="Select an iteration"
                            disabled={iterationOptions.length === 0}
                        />
                    </div>
                    <div style={{flex: 1}}>
                        <label>Select File:</label>
                        <div style={{display: "flex", gap: "5px"}}>
                            <Dropdown
                                fluid
                                selection
                                options={fileOptions}
                                value={selectedFileOptionValue}
                                onChange={handleFileChange}
                                placeholder={currentIterationData && fileOptions.length === 0 ? "No files in this iteration" : "Select a file to view"}
                                disabled={fileOptions.length === 0}
                                clearable={false}
                            />
                            {iterationsWithConversion.length > 0 && (
                                <Button
                                    icon="exchange"
                                    color="grey"
                                    size="small"
                                    onClick={() => setShowConversionComparisonModal(true)}
                                    title="Compare Results"
                                />
                            )}
                        </div>
                    </div>
                </div>

                {currentIterationData?.queuedEventConversionResponse && (
                    <Message positive={status === "OK"}
                             negative={status === "PERMANENT_FAILURE" || status === "TEMPORAL_FAILURE"}
                             style={{color: 'black', padding: '5px'}}>
                        <Message.Header>
                            Conversion Status: {status}
                            {statusMessage &&
                                <span style={{fontSize: '0.95em', marginLeft: '5px'}}> - {statusMessage}</span>}
                        </Message.Header>
                        {activeConverter &&
                            <p style={{margin: '2px 0', fontSize: '0.9em'}}>Converter: {activeConverter}</p>}
                        {selectedConversionFile &&
                            <p style={{margin: '2px 0', fontWeight: 'bold', fontSize: '0.9em'}}>Selected
                                File: {selectedConversionFile.fileName}</p>}
                    </Message>
                )}
            </div>

            {selectedConversionFile ? (
                <FileViewer
                    fileBuffer={selectedConversionFile.buffer}
                    fileName={selectedConversionFile.fileName}
                    displayName={false}
                />
            ) : (
                currentIterationData && fileOptions.length > 0 ? ( // If iteration selected and has files, but no file selected yet
                    <Message info style={{color: 'black'}}>
                        <Message.Header>No File Selected</Message.Header>
                        <p>Please select a file from the dropdown to view its contents, or one should be loading shortly.</p>
                    </Message>
                ) : (
                    <Message info style={{color: 'black'}}>
                        <Message.Header>No File to Display</Message.Header>
                        <p>Either no iteration with files is selected, or the selected iteration has no files.</p>
                    </Message>
                )
            )}
            {showConversionComparisonModal && (
                <ConversionResultsComparisonModal
                    isOpen={showConversionComparisonModal}
                    onClose={() => setShowConversionComparisonModal(false)}
                    aiAgentTask={props.aiAgentTask}
                />
            )}
        </>
    );
}